import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, MapPin, Briefcase, DollarSign, Clock } from 'lucide-react';
import { jobsAPI, countriesAPI } from '@/lib/api';

const JobsPage = () => {
  const [jobs, setJobs] = useState([]);
  const [countries, setCountries] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [jobsRes, countriesRes] = await Promise.all([
          jobsAPI.getAll(),
          countriesAPI.getAll(),
        ]);
        setJobs(jobsRes.data);
        setFilteredJobs(jobsRes.data);
        setCountries(countriesRes.data);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to fetch jobs:', error);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let filtered = jobs;
    if (searchTerm) {
      filtered = filtered.filter((job) =>
        job.title_fa.includes(searchTerm) || job.company_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (selectedCountry) {
      filtered = filtered.filter((job) => job.country_id === selectedCountry);
    }
    setFilteredJobs(filtered);
  }, [searchTerm, selectedCountry, jobs]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="text-[#D4AF37]">در حال بارگذاری...</div></div>;
  }

  return (
    <div className="min-h-screen py-12" data-testid="jobs-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-light text-white mb-4" data-testid="page-title">فرصت‌های شغلی</h1>
          <p className="text-zinc-400">بهترین موقعیت‌های شغلی در کشورهای مختلف</p>
        </div>

        {/* Filters */}
        <div className="max-w-4xl mx-auto mb-12 space-y-4">
          <div className="relative">
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500" size={20} />
            <input
              type="text"
              placeholder="جستجوی شغل..."
              data-testid="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-full px-12 py-4 text-white placeholder:text-zinc-500 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
            />
          </div>
          <select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            data-testid="country-filter"
            className="w-full bg-white/5 border border-white/10 rounded-full px-6 py-4 text-white focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
          >
            <option value="">همه کشورها</option>
            {countries.map((country) => (
              <option key={country.id} value={country.id}>{country.name_fa}</option>
            ))}
          </select>
        </div>

        {/* Jobs List */}
        <div className="space-y-4">
          {filteredJobs.map((job) => (
            <div
              key={job.id}
              data-testid={`job-card-${job.id}`}
              className="glass-effect border border-white/10 rounded-2xl p-6 hover:border-[#D4AF37]/30 transition-all gold-glow-hover"
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-sm text-[#D4AF37] mb-2">
                    <Briefcase size={16} />
                    <span>{job.company_name}</span>
                  </div>
                  <h3 className="text-xl font-medium text-white mb-2">{job.title_fa}</h3>
                  <div className="flex flex-wrap gap-4 text-sm text-zinc-400">
                    <div className="flex items-center gap-1">
                      <MapPin size={14} />
                      <span>{job.city_fa}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock size={14} />
                      <span>{job.job_type}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <DollarSign size={14} />
                      <span>{job.salary_range}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {job.requirements.slice(0, 3).map((req) => (
                      <span key={req} className="px-3 py-1 bg-white/5 rounded-full text-xs text-zinc-300">
                        {req}
                      </span>
                    ))}
                  </div>
                </div>
                <Link
                  to="/contact"
                  className="px-6 py-3 bg-[#D4AF37] text-black rounded-full font-medium hover:bg-[#F3E5AB] transition-colors whitespace-nowrap"
                >
                  درخواست مشاوره
                </Link>
              </div>
            </div>
          ))}
        </div>

        {filteredJobs.length === 0 && (
          <div className="text-center py-12">
            <p className="text-zinc-400">فرصت شغلی یافت نشد</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobsPage;